import { useMutation } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

export type EstimateOption = {
    Id: string;
    ProjectId: string;
    Label: string;
    Status: "ACTIVE" | "ALTERNATIVE" | "ARCHIVED";
    DriverCategory: string;
    DriverProductId: string | null;
    SortOrder: number;
};

export default function useEstimateOptions() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useList() {
        return useMutation({
            mutationFn: (params: { ProjectId: string }) =>
                axiosPrivate
                    .get(`/v1/estimate-options/list`, { params })
                    .then(parseSuccessResponseData),
        });
    }

    function useFromShingles() {
        return useMutation({
            mutationFn: (params: { ProjectId: string; ProductIds: string[] }) =>
                axiosPrivate
                    .post(`/v1/estimate-options/from-shingles`, params)
                    .then(parseSuccessResponseData),
        });
    }

    function useActivate() {
        return useMutation({
            mutationFn: (params: { Id: string }) =>
                axiosPrivate
                    .post(`/v1/estimate-options/activate`, params)
                    .then(parseSuccessResponseData),
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: (params: {
                Id: string;
                Label?: string;
                SortOrder?: number;
                Status?: "ALTERNATIVE" | "ARCHIVED";
            }) =>
                axiosPrivate
                    .post(`/v1/estimate-options/update`, params)
                    .then(parseSuccessResponseData),
        });
    }

    function useApplySuppliers() {
        return useMutation({
            mutationFn: (params: {
                ProjectId: string;
                Selections: Array<{ CategoryName: string; SupplierId: string }>;
            }) =>
                axiosPrivate
                    .post(`/v1/estimate-options/apply-suppliers`, params)
                    .then(parseSuccessResponseData),
        });
    }

    function useDelete() {
        return useMutation({
            mutationFn: (params: { Id: string }) =>
                axiosPrivate
                    .delete(`/v1/estimate-options/delete/${params.Id}`)
                    .then(parseSuccessResponseData),
        });
    }

    return {
        useList,
        useFromShingles,
        useActivate,
        useUpdate,
        useApplySuppliers,
        useDelete,
    };
}
